import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ContractInsightsData } from '@/pages/ContractDetail';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { ListPagination } from '@/components/ListPagination';
import { Badge } from '@/components/ui/badge';

import { Search, ChevronDown, ChevronRight, ShieldCheck, StickyNote } from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import { useOverdueInterest, type OverdueSale } from '@/hooks/useOverdueInterest';

interface Props {
    data: ContractInsightsData;
}

const PAGE_SIZE = 15;

interface InterestWeek {
    weekStart: Date;
    weekEnd: Date;
    expected: number;
    totalPaid: number;
    totalInterest: number;
    note: string | null;
    salesWithInterest: OverdueSale[];
}

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

/**
 * Below half a pesewa is float residue, not money. 0.005 is the engine's OWN dust constant — the
 * `if (finalAmt < 0.005) finalAmt = 0` residue guard in buildLoanPeriod [D-T1 rule 3], carried
 * verbatim by the in-database engine. Display only: no stored or computed value changes, an
 * amount this small simply stops printing as a signed, coloured, struck-through "-0.00".
 */
const DUST = 0.005;
const withoutDust = (amount: number) => (Math.abs(amount) < DUST ? 0 : amount);

/**
 * Formats overdue interest for display
 */
const formatInterest = (
    interest: number | null,
    applyOverdueInterest: boolean,
    surplusOverdueInterest: number | null
): React.ReactNode => {
    if (interest === null) {
        return <span className="text-muted-foreground">-</span>;
    }

    const elements: React.ReactNode[] = [];
    const shown = withoutDust(interest);
    const shownSurplus = surplusOverdueInterest === null ? null : withoutDust(surplusOverdueInterest);

    // If there's surplus, show it first (green, above the main value)
    if (shownSurplus !== null) {
        const surplusDisplay = shownSurplus === 0
            ? '0.00'
            : `+${shownSurplus.toFixed(2)}`;
        elements.push(
            <div key="surplus" className={cn(
                shownSurplus !== 0 && "text-green-600",
            )}>
                {surplusDisplay}
            </div>
        );
    }

    // Show the main interest value
    const isNegative = shown < 0;
    const displayValue = shown === 0
        ? '0.00'
        : isNegative
            ? shown.toFixed(2)
            : `+${shown.toFixed(2)}`;
    const colorClass = shown === 0
        ? ''
        : isNegative
            ? 'text-red-600'
            : 'text-green-600';

    // Waiver styling only where there is an amount to strike through. A waived sale carrying real
    // interest keeps its strike; a cell showing 0.00 has no waived amount to report, so striking
    // it would state a waiver over nothing.
    const waived = !applyOverdueInterest && shownSurplus === null && shown !== 0;

    elements.push(
        <div key="interest" className={cn(colorClass, waived && "line-through")}>
            {displayValue}
        </div>
    );

    return <div className="flex flex-col items-start">{elements}</div>;
};

/**
 * Formats the total interest for a week
 */
const formatWeekTotalInterest = (salesWithInterest: OverdueSale[]): React.ReactNode => {
    if (!salesWithInterest.length) {
        return <span className="text-muted-foreground">-</span>;
    }

    let totalInterest = 0;
    let hasAnyInterest = false;
    let hasAnyNegativeInterest = false;
    let allNegativeWaived = true;

    for (const sale of salesWithInterest) {
        if (sale.overdueInterest !== null) {
            hasAnyInterest = true;
            // Same dust floor as the cells below: a residue sale must not tip this week's total
            // into "negative and entirely waived" and strike a figure that is real money.
            const interest = withoutDust(sale.overdueInterest);
            totalInterest += interest;
            if (interest < 0) {
                hasAnyNegativeInterest = true;
                if (sale.applyOverdueInterest) {
                    allNegativeWaived = false;
                }
            }
        }
    }

    if (!hasAnyInterest) {
        return <span className="text-muted-foreground">-</span>;
    }

    const isNegative = totalInterest < 0;
    const displayValue = totalInterest === 0
        ? '0.00'
        : isNegative
            ? totalInterest.toFixed(2)
            : `+${totalInterest.toFixed(2)}`;

    const colorClass = totalInterest === 0
        ? ''
        : isNegative
            ? 'text-red-600'
            : 'text-green-600';

    return (
        <span className={cn(colorClass, hasAnyNegativeInterest && allNegativeWaived && "line-through")}>
            {displayValue}
        </span>
    );
};

export default function ContractInsightInterest({ data }: Props) {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
    const debouncedSearch = useDebounce(searchTerm, 300);

    const toggleWeekExpanded = (weekKey: string) => {
        setExpandedWeeks(prev => {
            const next = new Set(prev);
            if (next.has(weekKey)) {
                next.delete(weekKey);
            } else {
                next.add(weekKey);
            }
            return next;
        });
    };

    const contractId = data.contract?.id;

    // Reset to page 1 when search changes
    useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);

    // Overdue interest, priced by the database. The waiver and note queries below stay: they
    // feed this tab's badges, which the engine payload deliberately does not carry.
    const { totalOverdueInterest, weeks, sales, firstFullPaymentWeek } = useOverdueInterest(data);

    // Fetch waivers for this contract
    const { data: waivers } = useQuery({
        queryKey: ['contract-interest-waivers', contractId],
        queryFn: async () => {
            if (!contractId) return [];

            const { data, error } = await supabase
                .from('repayment_interest_waivers')
                .select('id, contract_id, period_start, period_end, reason')
                .eq('contract_id', contractId);

            if (error) throw error;
            return (data || []) as { id: number; contract_id: number; period_start: string; period_end: string; reason: string | null }[];
        },
        enabled: !!contractId,
    });

    // Fetch notes for this contract
    const { data: notes } = useQuery({
        queryKey: ['contract-interest-notes', contractId],
        queryFn: async () => {
            if (!contractId) return [];

            const { data, error } = await supabase
                .from('repayment_notes')
                .select('id, contract_id, period_start, period_end, reason')
                .eq('contract_id', contractId);

            if (error) throw error;
            return (data || []) as { id: number; contract_id: number; period_start: string; period_end: string; reason: string | null }[];
        },
        enabled: !!contractId,
    });




    // The interest schedule the server already grouped: one row per Monday-start week, the
    // Expected/Paid/Interest columns as the engine priced them. Weeks that priced nothing stay
    // hidden, as they always have — but the floor is half a pesewa rather than exact zero: the
    // engine computes on true doubles, so such a week lands on ±1e-13, and an exact test printed
    // it as a red, struck "-0.00" row. 0.005 is the engine's own dust constant [D-T1 rule 3].
    const interestSchedule = useMemo((): InterestWeek[] => {
        const rows: InterestWeek[] = weeks
            .filter(week => Math.abs(week.totalInterest) >= DUST)
            .map(week => ({
                weekStart: week.monday,
                weekEnd: addDays(week.monday, 6),
                expected: week.expected,
                totalPaid: week.totalPaid,
                totalInterest: week.totalInterest,
                note: null,
                salesWithInterest: week.sales,
            }));

        // Add notes to weeks. period_start/period_end are DATE strings: parseISO reads them on
        // the local calendar, which is the calendar these week Mondays live on.
        for (const note of data.repayment_notes || []) {
            const noteStart = parseISO(note.period_start);
            const noteEnd = parseISO(note.period_end);
            for (const week of rows) {
                if (week.weekStart >= noteStart && week.weekStart <= noteEnd) {
                    week.note = note.reason;
                }
            }
        }

        // Most recent week first
        return rows.sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());
    }, [weeks, data.repayment_notes]);

    // Filter by search term
    const filteredSchedule = useMemo(() => {
        if (!debouncedSearch) return interestSchedule;

        const searchLower = debouncedSearch.toLowerCase();
        return interestSchedule.filter(week =>
            week.salesWithInterest.some(s =>
                s.description?.toLowerCase().includes(searchLower) ||
                s.remarks?.toLowerCase().includes(searchLower)
            ) ||
            week.note?.toLowerCase().includes(searchLower)
        );
    }, [interestSchedule, debouncedSearch]);

    // Pagination
    const totalCount = filteredSchedule.length;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const paginatedSchedule = filteredSchedule.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    // Stats for Interest tab. No fallback rate: contracts.overdue_interest_rate is NOT NULL in the
    // database, so an absent value means the payload is stale or partial. The card renders the
    // absent state — the same '-' the Interest Start Date card uses — instead of showing a daily
    // rate derived from a number nobody set.
    const interestRate = data.contract?.overdue_interest_rate;
    const dailyInterestRate = typeof interestRate === 'number' && Number.isFinite(interestRate)
        ? `${(interestRate / 365).toFixed(2)}%`
        : '-';
    // Interest starts on the Monday after the first full payment week
    const interestStartDate = firstFullPaymentWeek
        ? addDays(firstFullPaymentWeek, 7)
        : null;
    const waiversCount = waivers?.length ?? 0;

    // Calculate surplus recovered (sum of all positive interest values where it was applied to service debt)
    const surplusRecovered = sales.reduce((sum, sale) => {
        if (sale.surplusOverdueInterest !== null && sale.surplusOverdueInterest > 0) {
            return sum + sale.surplusOverdueInterest;
        }
        if (sale.overdueInterest !== null && sale.overdueInterest > 0 && sale.applyOverdueInterest) {
            return sum + sale.overdueInterest;
        }
        return sum;
    }, 0);

    return (
        <div className="space-y-6">
            {/* Interest Summary Cards */}
            <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
                <StatCard
                    title="Overdue Interest"
                    value={<CurrencyDisplay amount={Math.abs(totalOverdueInterest)} />}
                    description="The total accumulated interest from weeks where payments were less than the expected amount. This is recalculated based on all payment history and waivers."
                />
                <StatCard
                    title="Daily Interest Rate"
                    value={dailyInterestRate}
                    description="This rate is applied daily to any outstanding balance when payments fall short of the expected weekly amount."
                />
                <StatCard
                    title="Interest Start Date"
                    value={interestStartDate ? format(interestStartDate, 'MMM d, yyyy') : '-'}
                    description="The date when overdue interest begins to accrue. This is the Monday after the first week where the full expected payment was made."
                />
                <StatCard
                    title="Waivers Applied"
                    value={waiversCount}
                    description="The number of interest waiver periods applied to this contract. During waived periods, overdue interest is not charged even if payments fall short."
                />
                <StatCard
                    title="Surplus Recovered"
                    value={<CurrencyDisplay amount={surplusRecovered} />}
                    description="When overpayments occur after a shortfall, the surplus is used to offset previously accrued interest. This shows the total interest recovered from surplus payments."
                />
            </div>

            {/* Interest Table */}
            <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {totalPages > 0 && (
                        <ListPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                        />
                    )}
                </div>

                {paginatedSchedule.length > 0 ? (
                    <>
                        {/* Mobile: Card layout */}
                        <div className="lg:hidden space-y-3">
                            {paginatedSchedule.map((week) => {
                                const weekKey = week.weekStart.toISOString();
                                const isExpanded = expandedWeeks.has(weekKey);
                                const hasMultipleSales = week.salesWithInterest.length > 1;

                                const startStr = format(week.weekStart, 'yyyy-MM-dd');
                                const endStr = format(week.weekEnd, 'yyyy-MM-dd');
                                const weekWaiver = waivers?.find(w => startStr <= w.period_end && endStr >= w.period_start);
                                const weekNote = notes?.find(n => startStr <= n.period_end && endStr >= n.period_start);

                                return (
                                    <div
                                        key={weekKey}
                                        className={cn(
                                            "p-4 rounded-xl border border-border/40 bg-card flex flex-col gap-3 shadow-sm transition-all",
                                            hasMultipleSales && "cursor-pointer hover:border-primary/30"
                                        )}
                                        onClick={() => hasMultipleSales && toggleWeekExpanded(weekKey)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex flex-col gap-1.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-sm">
                                                        {format(week.weekStart, 'MMM d')} - {format(week.weekEnd, 'MMM d, yy')}
                                                    </span>
                                                    {hasMultipleSales && (
                                                        <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                                            {week.salesWithInterest.length} entries
                                                            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center flex-wrap gap-1.5 mt-0.5">
                                                    {weekWaiver && (
                                                        <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 text-[10px] px-1.5 h-5 gap-1">
                                                            <ShieldCheck className="h-3 w-3" /> Waived
                                                        </Badge>
                                                    )}
                                                    {weekNote && (
                                                        <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 text-[10px] px-1.5 h-5 gap-1">
                                                            <StickyNote className="h-3 w-3" /> Note
                                                        </Badge>
                                                    )}
                                                    {week.note && (
                                                        <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                                                            • {week.note}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-end gap-1 text-right">
                                                <div className="text-xs text-muted-foreground">
                                                    Expected <span className="font-medium text-foreground">{formatCurrency(week.expected)}</span>
                                                </div>
                                                <div className="text-sm font-medium">
                                                    Paid: {formatCurrency(week.totalPaid)}
                                                </div>
                                                <div className="text-sm font-bold mt-0.5">
                                                    {formatWeekTotalInterest(week.salesWithInterest)}
                                                </div>
                                            </div>
                                        </div>

                                        {hasMultipleSales && isExpanded && (
                                            <div className="mt-2 pt-3 border-t border-border/50 flex flex-col gap-3">
                                                {week.salesWithInterest.map((sale) => {
                                                    const isDummySale = sale.id === -1;
                                                    return (
                                                        <div key={`${sale.id}-${sale.paid_at.getTime()}`} className="flex items-start justify-between text-sm pl-2 border-l-2 border-muted">
                                                            <div className="flex flex-col gap-0.5">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-muted-foreground text-xs font-medium">
                                                                        {format(sale.paid_at, 'MMM d, h:mm a')}
                                                                    </span>
                                                                    {isDummySale && (
                                                                        <span className="text-[10px] text-orange-600 font-medium bg-orange-50 px-1 rounded-sm">Accrual</span>
                                                                    )}
                                                                </div>
                                                                <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                                                                    {sale.description || '-'}
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-col items-end text-sm">
                                                                <span className="font-medium text-muted-foreground">{formatCurrency(sale.amount)}</span>
                                                                <div className="font-bold -mt-0.5">
                                                                    {formatInterest(
                                                                        sale.overdueInterest,
                                                                        sale.applyOverdueInterest,
                                                                        sale.surplusOverdueInterest
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Desktop: Table layout */}
                        <div className="hidden lg:block">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Week</TableHead>
                                        <TableHead>Expected</TableHead>
                                        <TableHead>Paid</TableHead>
                                        <TableHead>Interest</TableHead>
                                        <TableHead className="w-[200px]">Note</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedSchedule.map((week) => {
                                        const weekKey = week.weekStart.toISOString();
                                        const isExpanded = expandedWeeks.has(weekKey);
                                        const hasMultipleSales = week.salesWithInterest.length > 1;

                                        const startStr = format(week.weekStart, 'yyyy-MM-dd');
                                        const endStr = format(week.weekEnd, 'yyyy-MM-dd');
                                        const weekWaiver = waivers?.find(w => startStr <= w.period_end && endStr >= w.period_start);
                                        const weekNote = notes?.find(n => startStr <= n.period_end && endStr >= n.period_start);

                                        return (
                                            <Fragment key={weekKey}>
                                                <TableRow
                                                    className={cn(hasMultipleSales && "cursor-pointer hover:bg-muted/50")}
                                                    onClick={() => hasMultipleSales && toggleWeekExpanded(weekKey)}
                                                >
                                                    <TableCell className="text-muted-foreground whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            {hasMultipleSales && (
                                                                isExpanded
                                                                    ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                                    : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                            )}
                                                            {format(week.weekStart, 'MMM d')} - {format(week.weekEnd, 'MMM d, yyyy')}
                                                            {hasMultipleSales && (
                                                                <span className="text-xs text-muted-foreground">({week.salesWithInterest.length})</span>
                                                            )}
                                                            {weekWaiver && (
                                                                <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 text-[10px] px-1.5 h-5 gap-1">
                                                                    <ShieldCheck className="h-3 w-3" /> Waived
                                                                </Badge>
                                                            )}
                                                            {weekNote && (
                                                                <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 text-[10px] px-1.5 h-5 gap-1">
                                                                    <StickyNote className="h-3 w-3" /> Note
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {formatCurrency(week.expected)}
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {formatCurrency(week.totalPaid)}
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {formatWeekTotalInterest(week.salesWithInterest)}
                                                    </TableCell>
                                                    <TableCell className="max-w-[150px] truncate text-muted-foreground">
                                                        {week.note || '-'}
                                                    </TableCell>
                                                </TableRow>
                                                {/* Expanded rows showing all sales */}
                                                {hasMultipleSales && isExpanded && week.salesWithInterest.map((sale) => {
                                                    const isDummySale = sale.id === -1;
                                                    return (
                                                        <TableRow key={`${sale.id}-${sale.paid_at.getTime()}`} className="bg-muted/30 border-0">
                                                            <TableCell className="pl-10 text-sm text-muted-foreground">
                                                                ↳ {format(sale.paid_at, 'MMM d, h:mm a')}
                                                                {isDummySale && (
                                                                    <span className="ml-2 text-xs text-orange-600">(Monday accrual)</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell className="text-sm text-muted-foreground">
                                                                {formatCurrency(sale.amount)}
                                                            </TableCell>
                                                            <TableCell className="text-sm">
                                                                {formatInterest(
                                                                    sale.overdueInterest,
                                                                    sale.applyOverdueInterest,
                                                                    sale.surplusOverdueInterest
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                                                                {sale.description || '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-xl bg-muted/20">
                        <StickyNote className="h-8 w-8 mx-auto mb-3 opacity-30" />
                        <p className="text-sm font-medium">No interest entries</p>
                    </div>
                )}
            </div>
        </div>
    );
}
